import type { AgencyCategory } from '@saaga/shared/interfaces/agencies.interface'
import type { ReactNode } from 'react'
import type { UseFormReturn } from 'react-hook-form'

import { Badge } from '@/components/ui/badge'
import { FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { toggleInArray } from '@/lib/array.util'
import { cn } from '@/lib/utils'

import { agencyCategoryLabelMap } from '../agencies.mock'
import type { AgencyFormSchemaType } from '../agency-form.schema'

interface AgencyFormCategoriesSectionProps {
  form: UseFormReturn<AgencyFormSchemaType>
  categoryList: AgencyCategory[]
  isDisabled?: boolean
}

export const AgencyFormCategoriesSection = ({
  form,
  categoryList,
  isDisabled = false,
}: AgencyFormCategoriesSectionProps): ReactNode => {
  const selectedCategoryList = form.watch('categoryList') ?? []

  const handleCategoryToggle = (category: AgencyCategory): void => {
    const currentList = form.getValues('categoryList') ?? []
    form.setValue('categoryList', toggleInArray(currentList, category), {
      shouldValidate: true,
      shouldDirty: true,
    })
  }

  return (
    <FormField
      control={form.control}
      name="categoryList"
      render={() => (
        <FormItem>
          <FormLabel>Categories</FormLabel>
          <div className="agency-form-categories flex flex-wrap gap-2">
            {categoryList.map((category) => {
              const isSelected = selectedCategoryList.includes(category)
              return (
                <Badge
                  key={category}
                  variant={isSelected ? 'default' : 'outline'}
                  className={cn(
                    'agency-form-category-badge cursor-pointer transition-colors',
                    !isDisabled && 'hover:bg-primary/90',
                    isDisabled && 'cursor-not-allowed opacity-50'
                  )}
                  onClick={() => !isDisabled && handleCategoryToggle(category)}
                >
                  {agencyCategoryLabelMap[category]}
                </Badge>
              )
            })}
          </div>
          <FormDescription>Select one or more categories</FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />
  )
}

AgencyFormCategoriesSection.displayName = 'AgencyFormCategoriesSection'
